/**
 * useSplitCart Hook
 * Sepet bölme işlemleri için hook
 * Normal hızlı satış ve restoran hızlı satış için ortak kullanılır
 */

import { useState, useCallback, useMemo } from 'react';
import type { CartItem } from '../types/product.types';
import type {
  SplitCartState,
  RestaurantSplitCartState,
  UseSplitCartReturn,
  UseRestaurantSplitCartReturn,
  CartTotals,
} from '../types/splitCart.types';
import {
  restaurantSplitApi,
  restaurantActiveSaleApi,
  type RestaurantActiveSaleDto,
} from '../../../../services/restaurantApiService';

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Sepet toplamlarını hesapla
 * @param items - Sepet ürünleri
 * @param isRestaurantMode - Restoran modu mu? (Restoran modunda tüm fiyatlar KDV dahil kabul edilir)
 * 
 * ✅ DÜZELTME: Her ürünün vatStatus özelliğine bakılarak KDV hesaplanır
 * - vatStatus === 'included' → Fiyat KDV dahil → KDV EKLENMİYOR
 * - vatStatus === 'excluded' veya undefined → Fiyat KDV hariç → KDV EKLENİYOR
 * - isRestaurantMode === true ise → Tüm ürünler KDV dahil kabul edilir (vatStatus'e bakılmaz)
 */
export const calculateCartTotals = (items: CartItem[], isRestaurantMode: boolean = false): CartTotals => {
  let subtotal = 0;
  let taxTotal = 0;
  let discountTotal = 0;

  items.forEach((item) => {
    const itemTotal = item.price * item.quantity;
    
    // ✅ DÜZELTME: Her ürün için ayrı ayrı vatStatus kontrolü yap
    // Restoran modunda VEYA vatStatus='included' ise → KDV dahil fiyat
    const isItemVatIncluded = isRestaurantMode || item.vatStatus === 'included';
    
    if (isItemVatIncluded) {
      // Fiyat zaten KDV dahil → KDV ekleme, sadece geriye doğru hesapla (bilgi amaçlı)
      const vatRate = item.vatRate || 0;
      if (vatRate > 0) {
        taxTotal += itemTotal - (itemTotal / (1 + vatRate / 100));
      }
      subtotal += itemTotal;
    } else {
      // vatStatus === 'excluded' veya undefined → Fiyat KDV hariç → KDV ekle
      const vatAmount = itemTotal * ((item.vatRate || 0) / 100);
      taxTotal += vatAmount;
      subtotal += itemTotal + vatAmount;
    }
    
    // İndirim hesapla (originalPrice varsa, fiyat farkı = indirim)
    if (item.originalPrice && item.originalPrice > item.price) {
      discountTotal += (item.originalPrice - item.price) * item.quantity;
    }
  });

  return {
    subtotal,
    taxTotal,
    discountTotal,
    grandTotal: subtotal - discountTotal,
    itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
  };
};

/**
 * Fiş numarası oluştur
 */
export const generateInvoiceNumber = (prefix: string = 'QS'): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `${prefix}-${year}${month}${day}-${random}`;
};

// ============================================
// NORMAL HIZLI SATIŞ İÇİN HOOK
// ============================================

export interface UseSplitCartParams {
  /** Mevcut sepet */
  cart: CartItem[];
  /** Sepet güncelleme fonksiyonu */
  setCart: (cart: CartItem[]) => void;
  /** Mevcut fiş numarası */
  currentInvoiceNumber: string;
  /** Snackbar gösterme fonksiyonu */
  showSnackbar: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

/**
 * Normal hızlı satış için sepet bölme hook'u
 * Local state ile çalışır, SQL kaydı yapmaz
 */
export const useSplitCart = ({
  cart,
  setCart,
  currentInvoiceNumber,
  showSnackbar,
}: UseSplitCartParams): UseSplitCartReturn => {
  // Split cart state
  const [splitCartState, setSplitCartState] = useState<SplitCartState>({
    isActive: false,
    mainCart: [],
    splitCart: [],
    mainInvoiceNumber: '',
    splitInvoiceNumber: '',
    mainTotal: 0,
    splitTotal: 0,
  });

  /**
   * Sepet bölmeyi başlat
   */
  const initiateSplit = useCallback(async () => {
    if (cart.length === 0) {
      showSnackbar('Sepet boş! Önce ürün ekleyin.', 'warning');
      return;
    }

    // Yeni bölünmüş sepet için fiş numarası oluştur
    const splitInvoiceNumber = generateInvoiceNumber('QS');

    // State'i güncelle
    setSplitCartState({
      isActive: true,
      mainCart: [...cart],
      splitCart: [],
      mainInvoiceNumber: currentInvoiceNumber,
      splitInvoiceNumber,
      mainTotal: calculateCartTotals(cart).grandTotal,
      splitTotal: 0,
    });

    showSnackbar('Sepet bölme modu aktif!', 'info');
  }, [cart, currentInvoiceNumber, showSnackbar]);

  /**
   * Ürünü sağ sepete taşı
   */
  const moveItemToSplit = useCallback((itemId: string, quantity?: number) => {
    setSplitCartState((prev) => {
      if (!prev.isActive) return prev;

      // Kaynak ürünü bul
      const sourceItem = prev.mainCart.find((item) => item.id === itemId);
      if (!sourceItem) {
        showSnackbar('Ürün bulunamadı!', 'error');
        return prev;
      }

      const moveQty = quantity ?? sourceItem.quantity;
      if (moveQty > sourceItem.quantity) {
        showSnackbar('Taşınacak miktar mevcut miktardan fazla olamaz!', 'error');
        return prev;
      }

      let newMainCart = [...prev.mainCart];
      let newSplitCart = [...prev.splitCart];

      if (moveQty === sourceItem.quantity) {
        // Tümünü taşı
        newMainCart = newMainCart.filter((item) => item.id !== itemId);
        
        // Hedefte aynı ürün var mı?
        const existingInSplit = newSplitCart.find((item) => item.id === itemId);
        if (existingInSplit) {
          existingInSplit.quantity += moveQty;
        } else {
          newSplitCart.push({ ...sourceItem });
        }
      } else {
        // Kısmi taşı
        const sourceIndex = newMainCart.findIndex((item) => item.id === itemId);
        newMainCart[sourceIndex] = {
          ...sourceItem,
          quantity: sourceItem.quantity - moveQty,
        };

        // Hedefte aynı ürün var mı?
        const existingInSplit = newSplitCart.find((item) => item.id === itemId);
        if (existingInSplit) {
          existingInSplit.quantity += moveQty;
        } else {
          newSplitCart.push({
            ...sourceItem,
            quantity: moveQty,
          });
        }
      }

      return {
        ...prev,
        mainCart: newMainCart,
        splitCart: newSplitCart,
        mainTotal: calculateCartTotals(newMainCart).grandTotal,
        splitTotal: calculateCartTotals(newSplitCart).grandTotal,
      };
    });
  }, [showSnackbar]);

  /**
   * Ürünü sol sepete taşı
   */
  const moveItemToMain = useCallback((itemId: string, quantity?: number) => {
    setSplitCartState((prev) => {
      if (!prev.isActive) return prev;

      // Kaynak ürünü bul
      const sourceItem = prev.splitCart.find((item) => item.id === itemId);
      if (!sourceItem) {
        showSnackbar('Ürün bulunamadı!', 'error');
        return prev;
      }

      const moveQty = quantity ?? sourceItem.quantity;
      if (moveQty > sourceItem.quantity) {
        showSnackbar('Taşınacak miktar mevcut miktardan fazla olamaz!', 'error');
        return prev;
      }

      let newMainCart = [...prev.mainCart];
      let newSplitCart = [...prev.splitCart];

      if (moveQty === sourceItem.quantity) {
        // Tümünü taşı
        newSplitCart = newSplitCart.filter((item) => item.id !== itemId);
        
        // Hedefte aynı ürün var mı?
        const existingInMain = newMainCart.find((item) => item.id === itemId);
        if (existingInMain) {
          existingInMain.quantity += moveQty;
        } else {
          newMainCart.push({ ...sourceItem });
        }
      } else {
        // Kısmi taşı
        const sourceIndex = newSplitCart.findIndex((item) => item.id === itemId);
        newSplitCart[sourceIndex] = {
          ...sourceItem,
          quantity: sourceItem.quantity - moveQty,
        };

        // Hedefte aynı ürün var mı?
        const existingInMain = newMainCart.find((item) => item.id === itemId);
        if (existingInMain) {
          existingInMain.quantity += moveQty;
        } else {
          newMainCart.push({
            ...sourceItem,
            quantity: moveQty,
          });
        }
      }

      return {
        ...prev,
        mainCart: newMainCart,
        splitCart: newSplitCart,
        mainTotal: calculateCartTotals(newMainCart).grandTotal,
        splitTotal: calculateCartTotals(newSplitCart).grandTotal,
      };
    });
  }, [showSnackbar]);

  /**
   * Sepetleri birleştir
   */
  const mergeCarts = useCallback(() => {
    setSplitCartState((prev) => {
      if (!prev.isActive) return prev;

      // Tüm ürünleri ana sepete taşı
      const mergedCart = [...prev.mainCart];
      
      prev.splitCart.forEach((splitItem) => {
        const existingInMain = mergedCart.find((item) => item.id === splitItem.id);
        if (existingInMain) {
          existingInMain.quantity += splitItem.quantity;
        } else {
          mergedCart.push({ ...splitItem });
        }
      });

      // Ana sepeti güncelle
      setCart(mergedCart);

      showSnackbar('Sepetler birleştirildi!', 'success');

      return {
        isActive: false,
        mainCart: [],
        splitCart: [],
        mainInvoiceNumber: '',
        splitInvoiceNumber: '',
        mainTotal: 0,
        splitTotal: 0,
      };
    });
  }, [setCart, showSnackbar]);

  /**
   * Sol sepet ödemesi yap
   */
  const processMainPayment = useCallback(async (
    paymentType: 'cash' | 'pos' | 'partial',
    paymentDetails?: any
  ): Promise<boolean> => {
    if (!splitCartState.isActive || splitCartState.mainCart.length === 0) {
      showSnackbar('Sol sepet boş!', 'warning');
      return false;
    }

    // TODO: Ödeme işlemi burada yapılacak
    // Bu fonksiyon QuickSaleScreen'deki ödeme fonksiyonlarını çağıracak
    
    console.log('💰 Sol sepet ödemesi:', {
      paymentType,
      paymentDetails,
      invoiceNumber: splitCartState.mainInvoiceNumber,
      total: splitCartState.mainTotal,
      items: splitCartState.mainCart,
    });

    return true;
  }, [splitCartState, showSnackbar]);

  /**
   * Sağ sepet ödemesi yap
   */
  const processSplitPayment = useCallback(async (
    paymentType: 'cash' | 'pos' | 'partial',
    paymentDetails?: any
  ): Promise<boolean> => {
    if (!splitCartState.isActive || splitCartState.splitCart.length === 0) {
      showSnackbar('Sağ sepet boş!', 'warning');
      return false;
    }

    // TODO: Ödeme işlemi burada yapılacak
    // Bu fonksiyon QuickSaleScreen'deki ödeme fonksiyonlarını çağıracak
    
    console.log('💰 Sağ sepet ödemesi:', {
      paymentType,
      paymentDetails,
      invoiceNumber: splitCartState.splitInvoiceNumber,
      total: splitCartState.splitTotal,
      items: splitCartState.splitCart,
    });

    return true;
  }, [splitCartState, showSnackbar]);

  /**
   * Sepet bölmeyi iptal et
   */
  const cancelSplit = useCallback(() => {
    setSplitCartState((prev) => {
      if (!prev.isActive) return prev;

      // Tüm ürünleri ana sepete geri koy
      const mergedCart = [...prev.mainCart];
      
      prev.splitCart.forEach((splitItem) => {
        const existingInMain = mergedCart.find((item) => item.id === splitItem.id);
        if (existingInMain) {
          existingInMain.quantity += splitItem.quantity;
        } else {
          mergedCart.push({ ...splitItem });
        }
      });

      // Ana sepeti güncelle
      setCart(mergedCart);

      showSnackbar('Sepet bölme iptal edildi!', 'info');

      return {
        isActive: false,
        mainCart: [],
        splitCart: [],
        mainInvoiceNumber: '',
        splitInvoiceNumber: '',
        mainTotal: 0,
        splitTotal: 0,
      };
    });
  }, [setCart, showSnackbar]);

  return {
    splitCartState,
    setSplitCartState,
    initiateSplit,
    moveItemToSplit,
    moveItemToMain,
    mergeCarts,
    processMainPayment,
    processSplitPayment,
    cancelSplit,
  };
};

// ============================================
// RESTORAN HIZLI SATIŞ İÇİN HOOK
// ============================================

export interface UseRestaurantSplitCartParams {
  /** Masa ID */
  tableId: number;
  /** Restoran ekran ID */
  restaurantScreenId: number;
  /** Şirket ID */
  companyId: string;
  /** Garson adı */
  waiterName?: string;
  /** Snackbar gösterme fonksiyonu */
  showSnackbar: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

/**
 * Restoran hızlı satış için sepet bölme hook'u
 * SQL ile senkronize çalışır
 */
export const useRestaurantSplitCart = ({
  tableId,
  restaurantScreenId,
  companyId,
  waiterName,
  showSnackbar,
}: UseRestaurantSplitCartParams): UseRestaurantSplitCartReturn => {
  const [splitState, setSplitState] = useState<RestaurantSplitCartState>({
    isActive: false,
    mainSaleId: 0,
    splitSaleIds: [],
    splitCount: 0,
    activeSplitIndex: 0,
  });

  const [allCarts, setAllCarts] = useState<RestaurantActiveSaleDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Sepetleri yükle
   * ✅ MULTI-TENANT: companyId ile güvenli sorgu
   */
  const refreshCarts = useCallback(async () => {
    if (!tableId) return;

    setIsLoading(true);
    try {
      // ✅ MULTI-TENANT: companyId parametresi ile güvenli sorgu
      const response = await restaurantSplitApi.getByTableId(tableId);
      
      if (response.Success) {
        setAllCarts(response.Data);
        setSplitState({
          isActive: response.SplitCount > 0,
          mainSaleId: response.MainSaleId,
          splitSaleIds: response.Data
            .filter((s) => s.SplitParentId === response.MainSaleId)
            .map((s) => s.Id),
          splitCount: response.SplitCount,
          activeSplitIndex: 0,
        });
      }
    } catch (error) {
      console.error('❌ Sepetler yüklenirken hata:', error);
    } finally {
      setIsLoading(false);
    }
  }, [tableId, companyId]);

  /**
   * Sepet bölmeyi başlat
   * ✅ MULTI-TENANT: companyId ile güvenli işlem
   */
  const initiateSplit = useCallback(async (): Promise<boolean> => {
    if (!tableId) {
      showSnackbar('Masa ID gerekli!', 'error');
      return false;
    }

    setIsLoading(true);
    try {
      // Fiş numarası oluştur
      const now = new Date();
      const reservedLongCode = `RS-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${Date.now()}`;
      const reservedShortCode = `RS-${Date.now().toString().slice(-4)}`;

      // ✅ MULTI-TENANT: companyId parametresi ile güvenli sepet bölme
      const response = await restaurantSplitApi.splitTable({
        TableId: tableId,
        OpenedBy: waiterName,
        ReservedLongCode: reservedLongCode,
        ReservedShortCode: reservedShortCode,
      });

      if (response.Success) {
        showSnackbar(response.Message, 'success');
        await refreshCarts();
        return true;
      } else {
        showSnackbar('Sepet bölme başarısız!', 'error');
        return false;
      }
    } catch (error: any) {
      console.error('❌ Sepet bölme hatası:', error);
      showSnackbar(error?.response?.data?.Message || 'Sepet bölünürken hata oluştu', 'error');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [tableId, companyId, waiterName, showSnackbar, refreshCarts]);

  /**
   * Ürün taşı
   * ✅ MULTI-TENANT: companyId ile güvenli işlem
   */
  const moveItem = useCallback(async (
    itemId: number,
    fromSaleId: number,
    toSaleId: number,
    quantity?: number
  ): Promise<boolean> => {
    setIsLoading(true);
    try {
      // ✅ MULTI-TENANT: companyId parametresi ile güvenli ürün taşıma
      const response = await restaurantSplitApi.moveItem({
        ItemId: itemId,
        FromSaleId: fromSaleId,
        ToSaleId: toSaleId,
        Quantity: quantity,
        MovedBy: waiterName,
      });

      if (response.Success) {
        showSnackbar(response.Message, 'success');
        await refreshCarts();
        return true;
      } else {
        showSnackbar('Ürün taşıma başarısız!', 'error');
        return false;
      }
    } catch (error: any) {
      console.error('❌ Ürün taşıma hatası:', error);
      showSnackbar(error?.response?.data?.Message || 'Ürün taşınırken hata oluştu', 'error');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [companyId, waiterName, showSnackbar, refreshCarts]);

  /**
   * Tek sepeti birleştir
   * ✅ MULTI-TENANT: companyId ile güvenli işlem
   */
  const mergeSingleCart = useCallback(async (splitSaleId: number): Promise<boolean> => {
    setIsLoading(true);
    try {
      // ✅ MULTI-TENANT: companyId parametresi ile güvenli sepet birleştirme
      const response = await restaurantSplitApi.merge(splitSaleId);

      if (response.Success) {
        showSnackbar(response.Message, 'success');
        await refreshCarts();
        return true;
      } else {
        showSnackbar('Sepet birleştirme başarısız!', 'error');
        return false;
      }
    } catch (error: any) {
      console.error('❌ Sepet birleştirme hatası:', error);
      showSnackbar(error?.response?.data?.Message || 'Sepet birleştirilirken hata oluştu', 'error');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [companyId, showSnackbar, refreshCarts]);

  /**
   * Tüm sepetleri birleştir
   * ✅ MULTI-TENANT: companyId ile güvenli işlem
   */
  const mergeAllCarts = useCallback(async (): Promise<boolean> => {
    if (!tableId) {
      showSnackbar('Masa ID gerekli!', 'error');
      return false;
    }

    setIsLoading(true);
    try {
      // ✅ MULTI-TENANT: companyId parametresi ile güvenli tüm sepetleri birleştirme
      const response = await restaurantSplitApi.mergeAll(tableId);

      if (response.Success) {
        showSnackbar(response.Message, 'success');
        await refreshCarts();
        return true;
      } else {
        showSnackbar('Sepetler birleştirilemedi!', 'error');
        return false;
      }
    } catch (error: any) {
      console.error('❌ Tüm sepetleri birleştirme hatası:', error);
      showSnackbar(error?.response?.data?.Message || 'Sepetler birleştirilirken hata oluştu', 'error');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [tableId, companyId, showSnackbar, refreshCarts]);

  return {
    splitState,
    allCarts,
    isLoading,
    initiateSplit,
    moveItem,
    mergeSingleCart,
    mergeAllCarts,
    refreshCarts,
  };
};

export default useSplitCart;
